import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useHistory, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Button, Card, cn, Notice, SelectField, TextField } from '@tcrd/ui';
import { Page } from '../../../components/Page';
import { VehicleIllustration } from '../../../components/VehicleIllustration';
import { useToast } from '../../../components/toast';
import { api, errorMessage } from '../../../lib/api';
import { findModel, MAKES, VEHICLE_CATALOG, VEHICLE_COLORS } from '../../../lib/vehicle-catalog';
import { FUEL_LABEL, VEHICLE_TYPE_LABEL, type VehicleDto } from './types';

interface FormValues {
  make: string;
  model: string;
  trim: string;
  year: string;
  color: string;
  plate: string;
  type: string;
  doors: string;
  passengerCapacity: string;
  luggageCapacity: string;
  fuel: string;
  airConditioning: boolean;
}

const OTHER = '__other';
const THIS_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: THIS_YEAR + 1 - 1990 + 1 }, (_, i) => String(THIS_YEAR + 1 - i));
const DOORS: Record<string, string> = { SEDAN: '4', SUV: '4', HATCHBACK: '4', MINIVAN: '4', VAN: '3', PICKUP: '4', LUXURY: '4', OTHER: '4' };

export function VehicleFormPage() {
  const { id } = useParams<{ id?: string }>();
  const editing = !!id && id !== 'new';
  const history = useHistory();
  const qc = useQueryClient();
  const toast = useToast();
  const existing = useQuery({ queryKey: ['vehicle', id], queryFn: () => api<VehicleDto>(`/vehicles/${id}`), enabled: editing });
  const { register, handleSubmit, reset, formState, watch, setValue } = useForm<FormValues>({
    defaultValues: { make: '', model: '', color: '', type: 'SEDAN', fuel: 'GASOLINE', doors: '4', passengerCapacity: '4', luggageCapacity: '2', airConditioning: true, year: String(THIS_YEAR) },
  });
  // Marca, modelo y color se eligen de la lista; "Otra/Otro" permite escribirlos a mano.
  const [manualMake, setManualMake] = useState(false);
  const [manualModel, setManualModel] = useState(false);
  const [manualColor, setManualColor] = useState(false);
  const [make, model, color, type, year] = watch(['make', 'model', 'color', 'type', 'year']);
  const models = VEHICLE_CATALOG[make] ?? [];

  useEffect(() => {
    const v = existing.data;
    if (!v) return;
    reset({
      ...v,
      trim: v.trim ?? '',
      year: String(v.year),
      doors: String(v.doors),
      passengerCapacity: String(v.passengerCapacity),
      luggageCapacity: String(v.luggageCapacity),
    });
    const inCatalog = !!VEHICLE_CATALOG[v.make];
    setManualMake(!inCatalog);
    setManualModel(!inCatalog || !findModel(v.make, v.model));
    setManualColor(!VEHICLE_COLORS.some((c) => c.name.toLowerCase() === v.color.toLowerCase()));
  }, [existing.data, reset]);

  const chooseMake = (value: string) => {
    if (value === OTHER) {
      setManualMake(true);
      setManualModel(true);
      setValue('make', '');
    } else {
      setManualMake(false);
      setManualModel(false);
      setValue('make', value, { shouldValidate: true });
    }
    setValue('model', '');
  };

  const chooseModel = (value: string) => {
    if (value === OTHER) {
      setManualModel(true);
      setValue('model', '');
      return;
    }
    setManualModel(false);
    setValue('model', value, { shouldValidate: true });
    // Con el modelo se completan el tipo, las puertas y los pasajeros (se pueden ajustar).
    const found = findModel(make, value);
    if (found) {
      setValue('type', found.type);
      setValue('doors', DOORS[found.type] ?? '4');
      setValue('passengerCapacity', String(found.seats));
      setValue('luggageCapacity', found.type === 'VAN' || found.type === 'MINIVAN' || found.type === 'SUV' ? '3' : '2');
    }
  };

  const onSubmit = handleSubmit(async (v) => {
    const body = {
      make: v.make.trim(),
      model: v.model.trim(),
      trim: v.trim || undefined,
      year: Number(v.year),
      color: v.color.trim(),
      plate: v.plate,
      type: v.type,
      doors: Number(v.doors),
      passengerCapacity: Number(v.passengerCapacity),
      luggageCapacity: Number(v.luggageCapacity),
      fuel: v.fuel,
      airConditioning: v.airConditioning,
    };
    try {
      const saved = await api<VehicleDto>(editing ? `/vehicles/${id}` : '/vehicles', { method: editing ? 'PATCH' : 'POST', body });
      await qc.invalidateQueries({ queryKey: ['vehicles'] });
      await qc.invalidateQueries({ queryKey: ['vehicle', saved.id] });
      toast(editing ? 'Vehículo actualizado' : 'Vehículo registrado. Agrega sus fotos.', 'success');
      history.replace(`/driver/vehicles/${saved.id}`);
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  });

  const err = formState.errors;
  return (
    <Page title={editing ? 'Editar vehículo' : 'Nuevo vehículo'} back="/driver/vehicles">
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        {/* Vista previa: ilustración del tipo de vehículo en el color elegido. */}
        <Card className="p-4 flex flex-col items-center gap-1">
          <VehicleIllustration type={type} color={color} className="w-full max-w-sm h-auto" title={`${make} ${model}`} />
          <p className="font-semibold text-center">{[make, model, year].filter(Boolean).join(' ') || 'Tu vehículo'}</p>
          <p className="text-sm text-[var(--text-muted)]">{[color, VEHICLE_TYPE_LABEL[type as keyof typeof VEHICLE_TYPE_LABEL]].filter(Boolean).join(' · ')}</p>
        </Card>
        {!editing && <Notice>Después de guardar podrás agregar las fotos reales del vehículo; los clientes verán esas fotos en lugar de la ilustración.</Notice>}

        <Card className="p-5 grid grid-cols-2 gap-3">
          <div className="col-span-2 sm:col-span-1 flex flex-col gap-2">
            <SelectField label="Marca" value={manualMake ? OTHER : make} onChange={(e) => chooseMake(e.target.value)} error={err.make && !manualMake ? 'Elige la marca' : undefined}>
              <option value="" disabled>Selecciona la marca</option>
              {MAKES.map((mk) => <option key={mk} value={mk}>{mk}</option>)}
              <option value={OTHER}>Otra marca (escribir)</option>
            </SelectField>
            <input type="hidden" {...register('make', { required: true })} />
            {manualMake && <TextField label="Escribe la marca" value={make} onChange={(e) => setValue('make', e.target.value, { shouldValidate: true })} error={err.make ? 'Escribe la marca' : undefined} autoFocus />}
          </div>
          <div className="col-span-2 sm:col-span-1 flex flex-col gap-2">
            {!manualMake && (
              <SelectField label="Modelo" value={manualModel ? OTHER : model} onChange={(e) => chooseModel(e.target.value)} disabled={!make} error={err.model && !manualModel ? 'Elige el modelo' : undefined}>
                <option value="" disabled>{make ? 'Selecciona el modelo' : 'Primero elige la marca'}</option>
                {models.map((x) => <option key={x.name} value={x.name}>{x.name}</option>)}
                <option value={OTHER}>Otro modelo (escribir)</option>
              </SelectField>
            )}
            <input type="hidden" {...register('model', { required: true })} />
            {(manualModel || manualMake) && <TextField label="Escribe el modelo" value={model} onChange={(e) => setValue('model', e.target.value, { shouldValidate: true })} error={err.model ? 'Escribe el modelo' : undefined} />}
          </div>
          <SelectField label="Año" {...register('year', { required: true })}>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </SelectField>
          <TextField label="Versión (opcional)" placeholder="Ej. GLS, Limited" {...register('trim')} />

          <div className="col-span-2 flex flex-col gap-2">
            <span className="text-sm font-medium">Color</span>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Color">
              {VEHICLE_COLORS.map((c) => {
                const on = !manualColor && color.toLowerCase() === c.name.toLowerCase();
                return (
                  <button
                    key={c.name}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    title={c.name}
                    onClick={() => (setManualColor(false), setValue('color', c.name, { shouldValidate: true }))}
                    className={cn('tap flex items-center gap-2 rounded-full border px-3 h-9 text-sm', on ? 'border-[var(--text)] bg-[var(--surface-2)] font-semibold' : 'border-[var(--border)]')}
                  >
                    <span className="size-4 rounded-full border border-black/20 flex items-center justify-center" style={{ background: c.hex }}>
                      {on && <Check className="size-3" style={{ color: c.name === 'Blanco' || c.name === 'Plateado' || c.name === 'Beige' || c.name === 'Amarillo' ? '#111' : '#fff' }} aria-hidden />}
                    </span>
                    {c.name}
                  </button>
                );
              })}
              <button type="button" role="radio" aria-checked={manualColor} onClick={() => (setManualColor(true), setValue('color', ''))} className={cn('tap rounded-full border px-3 h-9 text-sm', manualColor ? 'border-[var(--text)] bg-[var(--surface-2)] font-semibold' : 'border-[var(--border)]')}>
                Otro
              </button>
            </div>
            <input type="hidden" {...register('color', { required: true })} />
            {manualColor && <TextField label="Escribe el color" placeholder="Ej. Azul cielo" value={color} onChange={(e) => setValue('color', e.target.value, { shouldValidate: true })} autoFocus />}
            {err.color && <p className="text-xs text-brand-red">Elige el color</p>}
          </div>

          <TextField label="Placa" hint="Ej. A123456" autoCapitalize="characters" {...register('plate', { required: true })} error={err.plate ? 'Escribe la placa' : undefined} />
          <SelectField label="Tipo" {...register('type')}>
            {Object.entries(VEHICLE_TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </SelectField>
          <SelectField label="Combustible" {...register('fuel')}>
            {Object.entries(FUEL_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </SelectField>
          <TextField label="Puertas" type="number" min={2} max={6} {...register('doors')} />
          <TextField label="Pasajeros" type="number" min={1} max={50} {...register('passengerCapacity')} />
          <TextField label="Maletas" type="number" min={0} max={50} {...register('luggageCapacity')} />
          <label className="tap flex items-center gap-3 text-sm self-end pb-3">
            <input type="checkbox" className="size-5 accent-[#E5252A]" {...register('airConditioning')} /> Aire acondicionado
          </label>
        </Card>
        <Button type="submit" size="lg" loading={formState.isSubmitting}>{editing ? 'Guardar cambios' : 'Registrar vehículo'}</Button>
      </form>
    </Page>
  );
}
